import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

interface AccessCheckResult {
  hasAccess: boolean;
  reason?: 'user_limit_exceeded' | 'no_subscription' | 'blocked';
  message?: string;
  isLoading: boolean;
}

export const useUserAccessCheck = (): AccessCheckResult => {
  const { user } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ["user-access-check", user?.id],
    queryFn: async () => {
      if (!user) return { hasAccess: false, reason: 'no_subscription' as const };

      try {
        // Get user's profile to find their business
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("id, business_id")
          .eq("id", user.id)
          .single();

        if (profileError || !profile?.business_id) {
          // New user without business - allow access to create one
          return { hasAccess: true };
        }

        // Get user's role
        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .maybeSingle();

        const userRole = roleData?.role || 'staff';

        // Owner and admin always have access
        if (userRole === 'owner' || userRole === 'admin') {
          return { hasAccess: true };
        }

        // For staff, check if business has exceeded user limit
        // Get total allowed users for the business
        const { data: moduleData } = await supabase.rpc('get_business_resource_limit', {
          p_business_id: profile.business_id,
          p_resource_type: 'team_users'
        });

        const totalLimit = moduleData || 2; // Default 2 users

        // Count current team members (profiles with this business_id)
        const { count: currentUsers } = await supabase
          .from("profiles")
          .select("id", { count: 'exact', head: true })
          .eq("business_id", profile.business_id);

        // Get list of allowed users (ordered by creation date - oldest first)
        const { data: allProfiles } = await supabase
          .from("profiles")
          .select("id, created_at")
          .eq("business_id", profile.business_id)
          .order("created_at", { ascending: true });

        if (!allProfiles || allProfiles.length === 0) {
          return { hasAccess: true };
        }

        // Check if user is within the allowed limit
        const allowedUserIds = allProfiles.slice(0, totalLimit).map(p => p.id);
        const isWithinLimit = allowedUserIds.includes(user.id);

        if (!isWithinLimit) {
          return {
            hasAccess: false,
            reason: 'user_limit_exceeded' as const,
            message: `O limite de usuários (${totalLimit}) foi atingido. Entre em contato com o administrador.`
          };
        }

        return { hasAccess: true };
      } catch (err) {
        console.warn("Error checking user access, allowing access by default:", err);
        // On network error, allow access by default to prevent blocking users
        return { hasAccess: true };
      }
    },
    enabled: !!user,
    staleTime: 30000, // Cache for 30 seconds
    refetchOnWindowFocus: true,
    retry: 2,
    retryDelay: 1000,
  });

  return {
    hasAccess: data?.hasAccess ?? true,
    reason: data?.reason,
    message: data?.message,
    isLoading,
  };
};
